import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getRecentAttacks } from '@/lib/api/ransomware';
import type { RansomwareAttack } from '@/lib/types/ransomware';
import { COUNTRY_NAMES } from '@/lib/utils/countryMapping';
import { Users, Briefcase, ExternalLink, BarChart3 } from 'lucide-react';

export function RecentAttacks() {
  const [attacks, setAttacks] = useState<RansomwareAttack[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAttacks() {
      try {
        const data = await getRecentAttacks();
        setAttacks(data);
      } catch (error) {
        console.error('Error fetching recent attacks:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAttacks();
  }, []);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading recent attacks...</div>
        </CardContent>
      </Card>
    );
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  }


  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-ibm-condensed">Recent Attacks</h2>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"> {/* Increased gap */}
        {attacks.map((attack, index) => (
          <Card key={index}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{attack.victim}</CardTitle>
                {attack.url && (
                  <a
                    href={attack.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View Source"
                    className="text-muted-foreground hover:text-primary"
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {attack.group_name && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Adversary Group:</span>
                    <span className="text-sm">{attack.group_name}</span>
                  </div>
                )}
                {attack.domain && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Domain:</span>
                    <span className="text-sm font-medium">{attack.domain}</span>
                  </div>
                )}
                {attack.date && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Incident Date:</span>
                    <span className="text-sm">{formatDate(attack.date)}</span>
                  </div>
                )}
                {attack.added && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Date Reported:</span>
                    <span className="text-sm">{formatDate(attack.added)}</span>
                  </div>
                )}
                {attack.country && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Country:</span>
                    <span className="text-sm">{COUNTRY_NAMES[attack.country] || attack.country}</span>
                  </div>
                )}
                {attack.activity && attack.activity !== 'Not Found' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Activity Sector:</span>
                    <span className="text-sm">{attack.activity}</span>
                  </div>
                )}
                {/* Infostealer Data Section */}
                {attack.infostealer_details &&
                  (attack.infostealer_details.employees > 0 ||
                   attack.infostealer_details.users > 0 ||
                   attack.infostealer_details.employees_url > 0 ||
                   attack.infostealer_details.users_url > 0 ||
                   (attack.infostealer_details.infostealer_stats && Object.values(attack.infostealer_details.infostealer_stats).some(count => count > 0))
                  ) && (
                  <div className="pt-4 mt-4 border-t border-muted/50"> {/* Increased pt and mt */}
                    <h4 className="text-md font-semibold mb-3 flex items-center"> {/* Increased mb */}
                      <BarChart3 size={16} className="mr-2 text-indigo-500" />
                      Infostealer Data
                    </h4>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs"> {/* Increased gap-y */}
                      <div className="flex items-center" title="Affected Employees">
                        <Briefcase size={14} className="mr-1.5 text-muted-foreground" /> {/* Increased icon size and mr */}
                        <span className="mr-1">Employees:</span>
                        <span className="font-semibold">{attack.infostealer_details.employees}</span> {/* Made value bold */}
                      </div>
                      <div className="flex items-center" title="Affected Users">
                        <Users size={14} className="mr-1.5 text-muted-foreground" /> {/* Increased icon size and mr */}
                        <span className="mr-1">Users:</span>
                        <span className="font-semibold">{attack.infostealer_details.users}</span> {/* Made value bold */}
                      </div>
                      {attack.infostealer_details.employees_url > 0 && (
                        <div className="flex items-center" title="Employee Data URLs">
                          <ExternalLink size={14} className="mr-1.5 text-muted-foreground" /> {/* Increased icon size and mr */}
                          <span className="mr-1">Emp. URLs:</span>
                          <span className="font-semibold">{attack.infostealer_details.employees_url}</span> {/* Made value bold */}
                        </div>
                      )}
                      {attack.infostealer_details.users_url > 0 && (
                        <div className="flex items-center" title="User Data URLs">
                          <ExternalLink size={14} className="mr-1.5 text-muted-foreground" /> {/* Increased icon size and mr */}
                          <span className="mr-1">User URLs:</span>
                          <span className="font-semibold">{attack.infostealer_details.users_url}</span> {/* Made value bold */}
                        </div>
                      )}
                    </div>
                    {attack.infostealer_details.infostealer_stats && Object.keys(attack.infostealer_details.infostealer_stats).length > 0 && (
                      <div className="mt-3"> {/* Increased mt */}
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">Top Stealers:</p> {/* Increased mb */}
                        <div className="flex flex-wrap gap-1.5"> {/* Increased gap */}
                          {Object.entries(attack.infostealer_details.infostealer_stats)
                            .sort(([, a]: [string, number], [, b]: [string, number]) => b - a) // Sort by count descending
                            .slice(0, 5) // Show top 5
                            .map(([name, count]) => (
                              <Badge key={name} variant="secondary" className="text-xs font-normal px-1.5 py-0.5"> {/* Adjusted padding and font-weight */}
                                {name}: {count}
                              </Badge>
                            ))}
                        </div>
                      </div>
                    )}
                    {/* "Last Update" text removed as per request */}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}